import React, { useState, useMemo } from 'react';
import { ChevronRight, Globe } from 'lucide-react';
import useIsMobile from '@/hooks/use-is-mobile';
import { useGetLlmModels } from '@/modules/gpt-chats/hooks/use-agents';
import { useGetAllModels } from '@/modules/gpt-chats/hooks/use-aimodel';
import { UseFormReturn } from 'react-hook-form';
import { getProviderDisplayName } from '@/modules/gpt-chats/utils/aimodel-provider.utils';
import { ModelStatus } from '@/modules/gpt-chats/types/aimodel.service.type';
import { useTranslation } from 'react-i18next';

import chLogo from '@/assets/logos/ch-logo.svg';
import euLogo from '@/assets/logos/eu-logo.svg';
import azureLogo from '@/assets/logos/azure-color.svg';
import anthropicLogo from '@/assets/logos/anthropic.svg';
import deepseekLogo from '@/assets/logos/deepseek-color.svg';
import googleLogo from '@/assets/logos/google.png';
import mistralLogo from '@/assets/logos/mistral-color.svg';
import openrouterLogo from '@/assets/logos/open-router.svg';
import openaiLogo from '@/assets/logos/openai.svg';

const PROVIDER_LOGOS: Record<string, string> = {
  azure: azureLogo,
  anthropic: anthropicLogo,
  deepseek: deepseekLogo,
  google: googleLogo,
  mistral: mistralLogo,
  openrouter: openrouterLogo,
  openai: openaiLogo,
};

interface ModelSelectionProps {
  form: UseFormReturn<any>;
  modelType?: 'chat' | 'embedding';
  isRequired?: boolean;
  disabled?: boolean;
}

const REGION_LOGOS: Record<string, string> = {
  ch: chLogo,
  eu: euLogo,
};

export const ModelSelection: React.FC<ModelSelectionProps> = ({
  form,
  modelType = 'chat',
  isRequired = false,
  disabled = false,
}) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedCustomProvider, setSelectedCustomProvider] = useState<string | null>(null);

  const containerRef = React.useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  const renderRegionLogo = (region: string | undefined) => {
    if (!region) return null;

    if (region === 'global') {
      return <Globe className="h-4 w-4 text-medium-emphasis" />;
    }

    if (!REGION_LOGOS[region]) return null;

    return (
      <img
        src={REGION_LOGOS[region]}
        alt={region}
        className="h-6 w-6 aspect-square object-cover overflow-hidden"
      />
    );
  };

  const renderProviderLogo = (provider: string | undefined) => {
    if (!provider) return null;
    const lowerProvider = provider.toLowerCase();

    let logoSrc = '';
    if (lowerProvider.includes('openai') && !lowerProvider.includes('azure'))
      logoSrc = PROVIDER_LOGOS.openai;
    else if (lowerProvider.includes('azure')) logoSrc = PROVIDER_LOGOS.azure;
    else if (lowerProvider.includes('anthropic')) logoSrc = PROVIDER_LOGOS.anthropic;
    else if (lowerProvider.includes('google') || lowerProvider.includes('gemini'))
      logoSrc = PROVIDER_LOGOS.google;
    else if (lowerProvider.includes('deepseek')) logoSrc = PROVIDER_LOGOS.deepseek;
    else if (lowerProvider.includes('mistral')) logoSrc = PROVIDER_LOGOS.mistral;
    else if (lowerProvider.includes('openrouter')) logoSrc = PROVIDER_LOGOS.openrouter;

    if (logoSrc) {
      return <img src={logoSrc} alt={provider} className="h-5 w-5 shrink-0 object-contain" />;
    }
    return null;
  };

  React.useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSelectedCategory(null);
        setSelectedCustomProvider(null);
      }
    };
    if (open) window.addEventListener('mousedown', handleClick);
    return () => window.removeEventListener('mousedown', handleClick);
  }, [open]);

  const { data: blocksModelsData } = useGetLlmModels();
  const { data: customModelsData } = useGetAllModels({
    search: '',
    page: 1,
    page_size: 100,
    status: ModelStatus.VALID,
    is_active: true,
    model_type: modelType,
  });

  const customMap = useMemo(() => {
    const map: Record<
      string,
      {
        modelName: string;
        displayName: string;
        modelId: string;
        defaultTemp?: number;
        maxTokens?: number;
      }[]
    > = {};
    if (!customModelsData?.models) return map;
    for (const m of customModelsData.models) {
      const provider = m.Provider as string;
      if (!map[provider]) map[provider] = [];
      const caps = m.Capabilities as { default_temp?: number; max_tokens?: number } | undefined;
      map[provider].push({
        modelName: m.ModelName ?? '',
        displayName: m.DisplayName,
        modelId: m._id,
        defaultTemp: caps?.default_temp,
        maxTokens: caps?.max_tokens,
      });
    }
    return map;
  }, [customModelsData]);
  const customProviders = useMemo(() => Object.keys(customMap), [customMap]);

  const blocksMap = useMemo(() => {
    const map: Record<
      string,
      {
        modelName: string;
        modelLabel: string;
        providerId: string;
        providerLabel: string;
        defaultTemp?: number;
        maxTokens?: number;
        region?: string;
      }[]
    > = {};
    if (!blocksModelsData) return map;

    for (const item of blocksModelsData) {
      if (item.model_type !== modelType) continue;
      const providerKey = item.provider_label;
      if (!map[providerKey]) map[providerKey] = [];
      map[providerKey].push({
        modelName: item.model_name,
        modelLabel: item.model_name_label,
        providerId: item.provider,
        providerLabel: item.provider_label,
        defaultTemp: item.default_temp,
        maxTokens: item.max_tokens,
        region: item.region,
      });
    }
    return map;
  }, [blocksModelsData, modelType]);

  const blockProviders = useMemo(() => Object.keys(blocksMap), [blocksMap]);

  const categories = useMemo(() => {
    const cats = ['custom'];
    if (blocksModelsData && blocksModelsData.length > 0) {
      cats.push('blocks');
    }
    return cats;
  }, [blocksModelsData]);

  const toggleOpen = () => {
    if (disabled) return;
    setOpen(!open);
    if (open) {
      setSelectedCategory(null);
      setSelectedCustomProvider(null);
    }
  };
  const handleCategoryClick = (cat: string) => {
    setSelectedCategory(cat);
    setSelectedCustomProvider(null);
  };
  const handleCustomProviderClick = (provider: string) => setSelectedCustomProvider(provider);

  const handleModelSelect = (
    provider: string,
    modelName: string,
    isBlocks: boolean,
    modelId?: string,
    defaultTemp?: number,
    maxTokens?: number
  ) => {
    form.setValue('llm_config.model_name', modelName, { shouldValidate: false });
    form.setValue('llm_config.is_blocks_provider', isBlocks, { shouldValidate: false });
    form.setValue('llm_config.provider', provider, { shouldValidate: false });

    // Handle Temperature
    let finalTemp = defaultTemp ?? 0.7; // Default to 0.70 if null/undefined
    if (finalTemp > 1) finalTemp = 1; // Clamp to max 1
    if (finalTemp < 0) finalTemp = 0; // Clamp to min 0 (safety)
    form.setValue('llm_config.temperature', finalTemp);

    // Handle Max Tokens
    let finalTokens = maxTokens ?? 10000; // Default to 10000 if null/undefined
    if (finalTokens > 10000) finalTokens = 10000; // Clamp to max 10000
    if (finalTokens < 0) finalTokens = 0; // Clamp to min 0 (safety)
    form.setValue('llm_config.max_tokens', finalTokens);

    if (modelId) {
      form.setValue('llm_config.model_id', modelId, { shouldValidate: false });
    }
    form.trigger('llm_config');

    setOpen(false);
    setSelectedCategory(null);
    setSelectedCustomProvider(null);
  };

  const isCustomDisabled = !customModelsData?.models || customModelsData.models.length === 0;

  const renderCategories = () => (
    <div role="menu" className={`min-w-[180px] border-r p-2 ${isMobile ? 'w-full' : ''}`}>
      {categories.map((cat) => {
        const label = cat === 'custom' ? 'My Models' : cat === 'blocks' ? 'Blocks Models' : cat;
        const active = selectedCategory === cat;
        const disabled = cat === 'custom' && isCustomDisabled;
        return (
          <div
            key={cat}
            onClick={() => !disabled && handleCategoryClick(cat)}
            className={`flex cursor-pointer items-center justify-between rounded-md p-2 text-sm ${disabled ? 'cursor-not-allowed opacity-50' : ''} ${active && !disabled ? 'bg-primary/10 text-primary' : !disabled ? 'hover:bg-border hover:text-high-emphasis' : ''} `}
          >
            <span>{label}</span>
            <ChevronRight className="h-4 w-4" />
          </div>
        );
      })}
    </div>
  );

  const renderSecondPanel = () => {
    if (!selectedCategory) return null;

    if (selectedCategory === 'custom') {
      return (
        <div role="menu" className={`min-w-[180px] border-r p-2 ${isMobile ? 'w-full' : ''}`}>
          {customProviders.map((provider) => {
            const active = selectedCustomProvider === provider;
            return (
              <div
                key={provider}
                onClick={() => handleCustomProviderClick(provider)}
                className={`flex cursor-pointer items-center justify-between rounded-md p-2 text-sm ${
                  active ? 'bg-primary/10 text-primary' : 'hover:bg-border hover:text-high-emphasis'
                }`}
              >
                <div className="flex items-center gap-2">
                  {renderProviderLogo(provider)}
                  <span>{getProviderDisplayName(provider) || provider}</span>
                </div>
                <ChevronRight className="h-4 w-4" />
              </div>
            );
          })}
        </div>
      );
    }

    if (selectedCategory === 'blocks') {
      return (
        <div role="menu" className={`min-w-[180px] border-r p-2 ${isMobile ? 'w-full' : ''}`}>
          {blockProviders.map((providerLabel) => {
            const active = selectedCustomProvider === providerLabel;
            return (
              <div
                key={providerLabel}
                onClick={() => handleCustomProviderClick(providerLabel)}
                className={`flex cursor-pointer items-center justify-between rounded-md p-2 text-sm ${
                  active ? 'bg-primary/10 text-primary' : 'hover:bg-border hover:text-high-emphasis'
                }`}
              >
                <div className="flex items-center gap-2">
                  {renderProviderLogo(providerLabel)}
                  <span>{providerLabel}</span>
                </div>
                <ChevronRight className="h-4 w-4" />
              </div>
            );
          })}
        </div>
      );
    }

    return null;
  };

  const renderThirdPanel = () => {
    if (!selectedCategory || !selectedCustomProvider) return null;

    if (selectedCategory === 'custom') {
      const models = customMap[selectedCustomProvider] || [];
      return (
        <div role="menu" className={`min-w-[180px] p-2 ${isMobile ? 'w-full' : ''}`}>
          {models.map((m) => (
            <div
              key={m.modelName}
              onClick={() =>
                handleModelSelect(
                  selectedCustomProvider,
                  m.modelName,
                  false,
                  m.modelId,
                  m.defaultTemp,
                  m.maxTokens
                )
              }
              className="flex cursor-pointer items-center justify-between rounded-md p-2 text-sm hover:bg-border hover:text-high-emphasis"
            >
              <span className="break-words">{m.displayName}</span>
            </div>
          ))}
        </div>
      );
    }

    if (selectedCategory === 'blocks') {
      const models = blocksMap[selectedCustomProvider] || [];
      return (
        <div role="menu" className={`min-w-[180px] p-2 ${isMobile ? 'w-full' : ''}`}>
          {models.map((m) => {
            return (
              <div
                key={m.modelName}
                onClick={() =>
                  handleModelSelect(
                    m.providerId,
                    m.modelName,
                    true,
                    undefined,
                    m.defaultTemp,
                    m.maxTokens
                  )
                }
                className="flex cursor-pointer items-center justify-between rounded-md p-2 text-sm hover:bg-border hover:text-high-emphasis"
              >
                <div className="flex w-full items-center gap-4">
                  <span className="break-words">{m.modelLabel}</span>
                  {renderRegionLogo(m.region)}
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    return null;
  };

  const finalProviderId = form.watch('llm_config.provider');
  const finalModelName = form.watch('llm_config.model_name');
  const isBlocks = form.watch('llm_config.is_blocks_provider');

  let finalDisplayLabel = 'Select a model...';

  let selectedRegion: string | undefined = undefined;

  if (finalProviderId && finalModelName) {
    if (isBlocks) {
      outer: for (const providerLabel in blocksMap) {
        const models = blocksMap[providerLabel];
        for (const m of models) {
          if (m.providerId === finalProviderId && m.modelName === finalModelName) {
            finalDisplayLabel = `Blocks / ${m.providerLabel} / ${m.modelLabel}`;
            selectedRegion = m.region;
            break outer;
          }
        }
      }
    } else {
      const providerLabel = getProviderDisplayName(finalProviderId) || finalProviderId;
      const models = customMap[finalProviderId] || [];
      const found = models.find((m) => m.modelName === finalModelName);
      if (found) finalDisplayLabel = `My Model / ${providerLabel} / ${found.displayName}`;
    }
  }

  return (
    <div ref={containerRef} className="w-full">
      <div className="mb-2 flex items-center gap-2">
        <span className="text-sm font-medium">
          {t('SELECT_MODEL')}
          {isRequired && <span className="ml-1 text-red-500">*</span>}
        </span>
      </div>

      <div className="relative">
        <div
          onClick={toggleOpen}
          className={`flex items-center justify-between rounded-lg border bg-card px-4 py-2 ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-card/80'}`}
        >
          <div className="flex items-center gap-2 overflow-hidden">
            {renderProviderLogo(finalProviderId)}
            <div className="flex items-center gap-2 overflow-hidden">
              <span className="truncate text-base font-medium text-high-emphasis">
                {finalDisplayLabel}
              </span>
              {renderRegionLogo(selectedRegion)}
            </div>
          </div>

          <ChevronRight
            className={`h-4 w-4 ml-2 shrink-0 transition-transform ${open ? 'rotate-90' : ''}`}
          />
        </div>

        {open && (
          <div
            className={`absolute z-50 mt-2 rounded-md border bg-card shadow-lg ${
              isMobile
                ? 'left-0 right-0 flex max-h-[60vh] flex-col overflow-y-auto'
                : 'flex max-h-[400px] overflow-hidden'
            }`}
          >
            {renderCategories()}
            {renderSecondPanel()}
            {renderThirdPanel()}
          </div>
        )}
      </div>
    </div>
  );
};

export default ModelSelection;
